/**
 * Serviço Fairtrade — consome api.xatblog.net/prices (proxy Vite/Laravel + fallback).
 */

import { XAT_API } from '@/services/api-paths';
import {
  normalizeFairtradePricesResponse,
  type FairtradePowerRow,
} from '@/lib/fairtrade-catalog';
import { syncPriceActivityFromCatalog } from '@/services/priceActivity.service';

const DIRECT_PRICES_URL = 'https://api.xatblog.net/prices';
const CACHE_TTL_MS = 8 * 60 * 1000;

type CacheEntry = {
  rows: FairtradePowerRow[];
  fetchedAt: number;
};

let memoryCache: CacheEntry | null = null;
let inflight: Promise<FairtradePowerRow[]> | null = null;

const JSON_HEADERS = { Accept: 'application/json' };

async function fetchPricesJson(
  url: string,
  signal?: AbortSignal
): Promise<unknown | null> {
  try {
    const res = await fetch(url, { cache: 'no-store', headers: JSON_HEADERS, signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return null;
  }
}

function isFresh(entry: CacheEntry | null): entry is CacheEntry {
  return Boolean(entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS);
}

async function afterCatalogLoaded(rows: FairtradePowerRow[]): Promise<void> {
  try {
    await syncPriceActivityFromCatalog(rows);
  } catch {
    /* Price Activity não deve bloquear o catálogo */
  }
}

/**
 * Carrega o catálogo Fairtrade.
 * Cache em memória (TTL ~8 min). Não partilha `inflight` abortado (Strict Mode).
 */
export async function fetchFairtradeCatalog(
  signal?: AbortSignal,
  opts?: { force?: boolean }
): Promise<FairtradePowerRow[]> {
  if (!opts?.force && isFresh(memoryCache)) {
    void afterCatalogLoaded(memoryCache.rows);
    return memoryCache.rows;
  }

  if (!opts?.force && inflight) {
    try {
      return await inflight;
    } catch {
      /* se o inflight anterior falhou/abortou, segue com fetch novo */
    }
  }

  const run = (async () => {
    const endpoints = [XAT_API.prices(), DIRECT_PRICES_URL];
    let lastError: unknown = null;

    for (const url of endpoints) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      try {
        const data = await fetchPricesJson(url, signal);
        if (!data) continue;
        const rows = normalizeFairtradePricesResponse(data);
        if (rows.length === 0) continue;
        memoryCache = { rows, fetchedAt: Date.now() };
        void afterCatalogLoaded(rows);
        return rows;
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
          throw err;
        }
        lastError = err;
      }
    }

    if (memoryCache?.rows.length) {
      void afterCatalogLoaded(memoryCache.rows);
      return memoryCache.rows;
    }

    throw lastError instanceof Error
      ? lastError
      : new Error('Não foi possível carregar os preços do Fairtrade.');
  })();

  inflight = run.finally(() => {
    inflight = null;
  });

  return inflight;
}

export function clearFairtradeCatalogCache(): void {
  memoryCache = null;
}
